import {
  CATALOG_IMPORT_MAX_FILE_NAME,
  CATALOG_IMPORT_MAX_INSTRUCTIONS,
  CATALOG_IMPORT_MAX_RERUN_INSTRUCTIONS,
  CatalogImportContext,
  CatalogSectionMapRq,
  CommitCatalogImportRq,
  CreateCatalogImportRq,
  RerunCatalogPageRq,
  UpdateCatalogDraftRq,
  UpdateCatalogSectionMapsRq,
} from "../../../interfaces/CatalogImportObject";

// Wire bodies for the catalogue import API. They mirror the Java request
// classes; the only work done here is trimming text and dropping empty
// optionals, because the server treats "absent" as "unchanged / use default"
// and an empty string as a real value.

const trimmed = (value: string | undefined | null): string | undefined => {
  const text = value?.trim();
  return text ? text : undefined;
};

/** Removes keys whose value is undefined; keeps null, false, 0 and "". */
function compact<T extends object>(body: T): T {
  const out: Record<string, unknown> = {};
  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined) out[key] = value;
  });
  return out as T;
}

function buildContext(context: CatalogImportContext | undefined): CatalogImportContext | undefined {
  if (!context) return undefined;
  const sections = (context.sections ?? []).filter((s) => s.name?.trim());
  const built = compact<CatalogImportContext>({
    latitude: Number.isFinite(context.latitude) ? context.latitude : undefined,
    longitude: Number.isFinite(context.longitude) ? context.longitude : undefined,
    contactMode: trimmed(context.contactMode),
    listingType: trimmed(context.listingType),
    sellerName: trimmed(context.sellerName),
    sections: sections.length > 0 ? sections : undefined,
  });
  return Object.keys(built).length > 0 ? built : undefined;
}

export function buildCreateImportPayload(form: CreateCatalogImportRq): CreateCatalogImportRq {
  return compact<CreateCatalogImportRq>({
    businessId: form.businessId,
    fileName: form.fileName.trim().slice(0, CATALOG_IMPORT_MAX_FILE_NAME),
    fileSizeBytes: form.fileSizeBytes,
    sha256: form.sha256.toLowerCase(),
    pageCount: form.pageCount && form.pageCount > 0 ? form.pageCount : undefined,
    pageRanges: trimmed(form.pageRanges),
    instructions: trimmed(form.instructions)?.slice(0, CATALOG_IMPORT_MAX_INSTRUCTIONS),
    context: buildContext(form.context),
    allowDuplicate: form.allowDuplicate ? true : undefined,
  });
}

export function buildUpdateDraftPayload(form: UpdateCatalogDraftRq): UpdateCatalogDraftRq {
  return compact<UpdateCatalogDraftRq>({ ...form });
}

export function buildRerunPayload(form: RerunCatalogPageRq): RerunCatalogPageRq {
  return { instructions: form.instructions.trim().slice(0, CATALOG_IMPORT_MAX_RERUN_INSTRUCTIONS) };
}

export function buildSectionMapsPayload(maps: CatalogSectionMapRq[]): UpdateCatalogSectionMapsRq {
  return {
    maps: maps.map((row) =>
      compact<CatalogSectionMapRq>({
        sectionMapId: row.sectionMapId,
        sectionId: row.sectionId,
        newSectionName: trimmed(row.newSectionName),
        parentSectionId: row.parentSectionId,
      })
    ),
  };
}

export function buildCommitPayload(draftIds?: string[]): CommitCatalogImportRq {
  return draftIds && draftIds.length > 0 ? { draftIds } : {};
}
